import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useViewport } from "@bindeleddet/core/hooks";
import { Form, Typography, Input, Checkbox, Row, Col, Card, Select, Button, InputNumber, Divider } from "antd";
import { TinyDivider as StyledDivider } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { StudyProgram, StudentUser } from "@bindeleddet/core/types";
import axios from "axios";
import { PASSWORD_RULE } from "helpers/password";

const { Paragraph, Link, Title } = Typography;
const { Option } = Select;

const CreateStudentProfile = () => {
  const [studyPrograms, setStudyPrograms] = useState<StudyProgram[]>([]);
  const [loading, setLoading] = useState(false);

  const { width } = useViewport();
  const breakpoint = 768;

  const [form] = Form.useForm();
  const history = useHistory();

  useEffect(() => {
    if (studyPrograms.length > 0) return;
    axios.get<StudyProgram[]>(`/students/study-program/`).then(response => {
      const filteredStudyPrograms = response.data.filter(program => program.name !== "Alle"); // Alle not an option
      const indokIndex = filteredStudyPrograms.findIndex(obj => obj.id === 1);
      const indok = filteredStudyPrograms.splice(indokIndex, 1)[0];
      filteredStudyPrograms.unshift(indok); // Place indok first
      setStudyPrograms(filteredStudyPrograms);
    });
  }, [studyPrograms]);

  const createStudent = (values: StudentUser) => {
    setLoading(true);
    axios
      .post<StudentUser>(`/students/create-student-user/`, {
        first_name: values.first_name,
        last_name: values.last_name,
        username: values.username,
        password: values.password,
        email: `${values.username}@stud.ntnu.no`,
        mobile_number: values.mobile_number,
        study_programme: values.study_programme,
        year_level: values.year_level,
        allergies: values.allergies,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .then(() => {
        history.push("/");
        message("success", "Bekreftelsesmail sendt", "Trykk på lenken i mailen for å aktivere brukerprofilen din");
      })
      .catch(error => {
        const data = error.response?.data;
        // Check for direct message from backend
        if (data?.message) {
          message("error", data.message);
          // Check for field-specific errors (from serializer validation)
        } else if (data?.username?.[0]) {
          message("error", data.username[0]);
        } else if (data?.email?.[0]) {
          message("error", data.email[0]);
        } else {
          message("error", "Kunne ikke opprette profil. Prøv igjen senere.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Title style={{ margin: "20px auto", textAlign: "center" }}>Lag ny studentbruker</Title>
      <Card style={width < breakpoint ? { width: "95%", margin: "0 auto" } : { width: "70%", margin: "0 auto" }}>
        <Form form={form} layout="vertical" requiredMark="optional" onFinish={createStudent}>
          <Row gutter={[16, 1]}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label="Brukernavn (NTNU)"
                name="username"
                rules={[
                  {
                    required: true,
                    message: "Skriv inn brukernavn",
                  },
                  {
                    pattern: /^[a-z0-9æøå]+$/,
                    message: "Kun små bokstaver, uten mellomrom eller spesialtegn",
                  },
                ]}
                tooltip="Benytt ditt NTNU-brukernavn. Brukernavnet blir brukt til å automatisk sette mailadresse."
              >
                <Input addonAfter="@stud.ntnu.no" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="mobile_number"
                label="Mobilnummer"
                rules={[
                  { required: true, message: "Vennligst skriv inn telefonnummer" },
                  {
                    validator: (_, value) => {
                      if (!value || value.toString().length !== 8) {
                        return Promise.reject(new Error("Telefonnummeret må være 8 sifre"));
                      }
                      const fakeNumbers = [
                        "12345678",
                        "87654321",
                        "12341234",
                        "22225555",
                        "11111111",
                        "22222222",
                        "33333333",
                        "44444444",
                        "55555555",
                        "66666666",
                        "77777777",
                        "88888888",
                        "99999999",
                        "00000000",
                        "43214321",
                      ];
                      if (fakeNumbers.includes(value.toString())) {
                        return Promise.reject(new Error("Skriv inn et gyldig telefonnummer"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber style={{ width: "100%" }} maxLength={8} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="first_name"
                label="Fornavn"
                rules={[{ required: true, message: "Vennligst skriv inn fornavn" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="last_name"
                label="Etternavn"
                rules={[{ required: true, message: "Vennligst skriv inn etternavn" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="study_programme"
                label="Linje"
                rules={[{ required: true, message: "Vennligst velg linje" }]}
              >
                <Select
                  style={{ width: "100%" }}
                  placeholder="Velg linje..."
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.children as unknown) as string).toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {studyPrograms.map(program => (
                    <Option key={program.id} value={program.id}>
                      {program.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="year_level"
                label="Årskull"
                rules={[{ required: true, message: "Vennligst velg årskull" }]}
              >
                <Select placeholder="Velg årstrinn...">
                  <Option value={1}>1</Option>
                  <Option value={2}>2</Option>
                  <Option value={3}>3</Option>
                  <Option value={4}>4</Option>
                  <Option value={5}>5</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="allergies"
                label="Allergier"
                rules={[{ required: false }]}
                tooltip="Om du har allergier kan du skrive det inn her, slik at vi er klar over dette i forkant av arrangementer. Har du flere allergier, skill dem ved hjelp av komma."
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Passord"
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Vennligst fyll inn passord",
                  },
                  PASSWORD_RULE,
                ]}
                hasFeedback
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label="Bekreft passord"
                name="confirm"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: "Vennligst bekreft passordet",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("De to passordene samsvarer ikke"));
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>
            </Col>
            <Divider />
            <Col span={24}>
              <Form.Item
                name="agreement"
                label="Vilkår"
                valuePropName="checked"
                rules={[
                  {
                    required: true,
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error("Du må godta brukervilkårene")),
                  },
                ]}
              >
                <Checkbox>
                  <Paragraph style={{ margin: 0, cursor: "default" }}>
                    Jeg godtar Bindeleddet NTNU sine{" "}
                    <Link href="/user-terms/" target="_blank" style={{ textDecoration: "underline" }}>
                      brukervilkår
                    </Link>
                    .
                  </Paragraph>
                </Checkbox>
              </Form.Item>
            </Col>
            <StyledDivider width="90%" style={{ marginLeft: "5%" }} />
          </Row>
          <br />
          <Col span={24} style={{ textAlign: "center" }}>
            <Form.Item>
              <Button size="large" type="primary" style={{ width: "100%" }} htmlType="submit" loading={loading}>
                Opprett bruker
              </Button>
            </Form.Item>
          </Col>
        </Form>
      </Card>
    </>
  );
};

export default CreateStudentProfile;
